import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
interface MarkdownRendererProps {
  content: string;
  className?: string;
}
export function MarkdownRenderer({
  content,
  className = ''
}: MarkdownRendererProps) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} className={`prose prose-invert max-w-none ${className}`} components={{
    // Headings - Enhanced with better spacing and colors
    h1: ({
      children
    }) => <h1 className="text-2xl font-bold text-white mb-4 mt-6 first:mt-0 pb-2 border-b border-slate-700/50" data-id="element-832">
            {children}
          </h1>,
    h2: ({
      children
    }) => <h2 className="text-xl font-semibold text-white mb-3 mt-5 first:mt-0 pb-2 border-b border-slate-800/50" data-id="element-833">
            {children}
          </h2>,
    h3: ({
      children
    }) => <h3 className="text-lg font-semibold text-slate-100 mb-2 mt-4 first:mt-0" data-id="element-834">
            {children}
          </h3>,
    h4: ({
      children
    }) => <h4 className="text-base font-semibold text-slate-200 mb-2 mt-3 first:mt-0" data-id="element-835">
            {children}
          </h4>,
    // Paragraphs - Better line height and spacing
    p: ({
      children
    }) => <p className="text-sm text-slate-200 mb-4 last:mb-0 leading-relaxed" data-id="element-836">
            {children}
          </p>,
    // Lists - Enhanced styling
    ul: ({
      children
    }) => <ul className="list-disc list-outside space-y-2 mb-4 ml-5 text-slate-200" data-id="element-837">
            {children}
          </ul>,
    ol: ({
      children
    }) => <ol className="list-decimal list-outside space-y-2 mb-4 ml-5 text-slate-200" data-id="element-838">
            {children}
          </ol>,
    li: ({
      children
    }) => <li className="text-sm text-slate-200 leading-relaxed pl-1" data-id="element-839">
            {children}
          </li>,
    // Strong and Emphasis - Better contrast
    strong: ({
      children
    }) => <strong className="font-bold text-white" data-id="element-840">{children}</strong>,
    em: ({
      children
    }) => <em className="italic text-slate-100" data-id="element-841">{children}</em>,
    // Code - Enhanced with better colors
    code: ({
      inline,
      children
    }) => inline ? <code className="px-2 py-0.5 bg-slate-950/80 text-blue-300 rounded text-xs font-mono border border-slate-800/50" data-id="element-842">
              {children}
            </code> : <code className="block px-4 py-3 bg-slate-950/80 text-slate-200 rounded-lg text-xs font-mono overflow-x-auto mb-4 border border-slate-800/50" data-id="element-843">
              {children}
            </code>,
    pre: ({
      children
    }) => <pre className="bg-slate-950/80 rounded-lg p-4 mb-4 overflow-x-auto border border-slate-800/50 shadow-lg" data-id="element-844">
            {children}
          </pre>,
    // Links - Enhanced with better hover states
    a: ({
      href,
      children
    }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/40 hover:decoration-blue-300 transition-all font-medium" data-id="element-845">
            {children}
          </a>,
    // Tables - Enhanced with better borders and hover
    table: ({
      children
    }) => <div className="overflow-x-auto mb-4 rounded-lg border border-slate-800/50 shadow-lg" data-id="element-846">
            <table className="min-w-full divide-y divide-slate-800/50" data-id="element-847">
              {children}
            </table>
          </div>,
    thead: ({
      children
    }) => <thead className="bg-slate-900/60" data-id="element-848">{children}</thead>,
    tbody: ({
      children
    }) => <tbody className="divide-y divide-slate-800/50 bg-slate-950/30" data-id="element-849">
            {children}
          </tbody>,
    tr: ({
      children
    }) => <tr className="hover:bg-slate-800/30 transition-colors" data-id="element-850">
            {children}
          </tr>,
    th: ({
      children
    }) => <th className="px-4 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider" data-id="element-851">
            {children}
          </th>,
    td: ({
      children
    }) => <td className="px-4 py-3 text-sm text-slate-200" data-id="element-852">{children}</td>,
    // Blockquotes - Enhanced with gradient border
    blockquote: ({
      children
    }) => <blockquote className="border-l-4 border-blue-500 bg-slate-900/40 pl-4 py-3 mb-4 text-slate-300 italic rounded-r-lg" data-id="element-853">
            {children}
          </blockquote>,
    // Horizontal Rule - Enhanced
    hr: () => <hr className="border-slate-800/50 my-6 border-t-2" data-id="element-854" />
  }} data-id="element-831">
      {content}
    </ReactMarkdown>;
}